import type {WhoamiResponse} from '@/lib/auth-types';

export interface RoutePermissionRequirement {
  resource: string;
  action: string;
}

export interface RouteRule {
  /** Glob pattern (`*` = single segment, `**` = any number of segments). */
  pattern: string;
  requirement: RoutePermissionRequirement;
}

/**
 * Registry of route-pattern → required permission for **internal** users.
 * Routes not listed here are accessible to any authenticated internal user.
 * **External** users have a per-user whitelist on `WhoamiResponse.whitelisted_routes`
 * and don't consult this list at all.
 *
 * Add entries as feature routes are introduced, e.g.:
 *
 *   { pattern: "/jobs/**", requirement: { resource: "jobs", action: "view" } }
 *   { pattern: "/admin/users", requirement: { resource: "admin-users", action: "view" } }
 *
 * The matcher walks entries in array order and uses the first match. List
 * more-specific patterns before catch-alls.
 */
export const ROUTE_REQUIREMENTS: ReadonlyArray<RouteRule> = [
  // The journal-entry push queue. ONE key gates the whole surface -- seeing
  // the queue, correcting the coding and posting are one job, and splitting
  // the permission only produces people who cannot finish it (the API says
  // the same in `journal_entries/constants.py`).
  {pattern: '/journal-entries', requirement: {resource: 'journal_entries', action: 'manage'}},
  {pattern: '/journal-entries/**', requirement: {resource: 'journal_entries', action: 'manage'}},
  // Learning is open to every signed-in user, visitors included: the API picks
  // each person's videos. Only the per-module Progress tab is gated.
  {pattern: '/learning/*/progress', requirement: {resource: 'learning', action: 'view_progress'}},
  // Approval-chain editor: org-admin configuration only, now under the Admin
  // console (RE-1981). Blocked at the route guard for users without it
  // (they'd 403 on save anyway). Listed before the `/admin` floor below so
  // this finer `approvals:configure` gate wins over the section's `users:view`.
  {
    pattern: '/admin/approval-chains',
    requirement: {resource: 'approvals', action: 'configure'},
  },
  // Users & bank-details admin: bank details are admin-managed, so this
  // surface requires users:edit. Hidden from the sub-nav and blocked at the
  // route guard for users without it (they'd 403 on save anyway).
  {
    pattern: '/account/users',
    requirement: {resource: 'users', action: 'edit'},
  },
  // Visitors (subcontractor groups). Its own key, so the people who manage
  // subcontractors need not hold `users:view`. Listed before the `/admin`
  // floor so this finer gate wins on these paths.
  {pattern: '/admin/visitors', requirement: {resource: 'visitors', action: 'manage'}},
  {pattern: '/admin/visitors/**', requirement: {resource: 'visitors', action: 'manage'}},
  // Admin console landing + section floor. `users:view` for now (super-admins
  // always pass); finer gates are the per-page rules below + per-button checks.
  {pattern: '/admin', requirement: {resource: 'users', action: 'view'}},
  // Admin user-management console. The list/detail floor is `users:view`;
  // create/edit/delete actions are further gated per-button via
  // `usePermission` inside the pages (and 403 server-side regardless). Both
  // the bare path and the `/**` subtree are listed — the matcher's `**`
  // requires a trailing segment, so it wouldn't cover `/admin/users` itself.
  {pattern: '/admin/users', requirement: {resource: 'users', action: 'view'}},
  {pattern: '/admin/users/**', requirement: {resource: 'users', action: 'view'}},
  // Roles & permissions management. Read floor is `roles:view`; create/edit/
  // delete are gated server-side on `roles:edit` (and per-control in the page).
  {pattern: '/admin/roles', requirement: {resource: 'roles', action: 'view'}},
  {pattern: '/admin/roles/**', requirement: {resource: 'roles', action: 'view'}},
  // Audit log viewer — admin floor (`users:view`); org-scoped server-side.
  {pattern: '/admin/audit-log', requirement: {resource: 'users', action: 'view'}},
  {pattern: '/admin/audit-log/**', requirement: {resource: 'users', action: 'view'}},
  // Organizations management — super-admin only. No role grants
  // `organizations:manage`, so only super-admins (who bypass) reach it.
  {pattern: '/admin/organizations', requirement: {resource: 'organizations', action: 'manage'}},
  {
    pattern: '/admin/organizations/**',
    requirement: {resource: 'organizations', action: 'manage'},
  },
  // Own-org profile (org-admin self-service) — admin floor.
  {pattern: '/admin/organization-profile', requirement: {resource: 'users', action: 'view'}},
  {pattern: '/admin/organization-profile/**', requirement: {resource: 'users', action: 'view'}},
  // System settings (mileage rates, RE-1984). Read floor is `settings:view`;
  // create/edit/delete are gated server-side on `settings:edit` (and
  // per-control in the page). Org Admin holds both.
  {pattern: '/admin/settings', requirement: {resource: 'settings', action: 'view'}},
  {pattern: '/admin/settings/**', requirement: {resource: 'settings', action: 'view'}},
  // Transaction ledger — org-wide finance money-movement view (DERP-19).
  // Gated on `advance_requests:export` (Finance + CEO), mirroring the API
  // endpoint. Listed before the `/advances/**` catch-all so the finer export
  // gate wins over the section's broad `view` floor (which every Employee
  // holds) — a viewer would otherwise reach it by URL and 403 on fetch.
  {pattern: '/advances/ledger', requirement: {resource: 'advance_requests', action: 'export'}},
  // Core modules. Every current org user holds these via the Employee role,
  // so this is a no-op for them — it just keeps the rail honest for future
  // roles that don't.
  {pattern: '/advances', requirement: {resource: 'advance_requests', action: 'view'}},
  {pattern: '/advances/**', requirement: {resource: 'advance_requests', action: 'view'}},
  // Client create/edit need clients:edit — listed before the view catch-alls so
  // a viewer can't reach the form by URL (they'd 403 on submit anyway).
  {pattern: '/clients/new', requirement: {resource: 'clients', action: 'edit'}},
  {pattern: '/clients/*/edit', requirement: {resource: 'clients', action: 'edit'}},
  {pattern: '/clients', requirement: {resource: 'clients', action: 'view'}},
  {pattern: '/clients/**', requirement: {resource: 'clients', action: 'view'}},
  // Project create/edit need projects:edit — listed before the view catch-alls
  // so a viewer can't reach the form by URL (they'd 403 on submit anyway).
  // Delete is gated in-page on projects:delete.
  {pattern: '/projects/new', requirement: {resource: 'projects', action: 'edit'}},
  {pattern: '/projects/*/edit', requirement: {resource: 'projects', action: 'edit'}},
  {pattern: '/projects', requirement: {resource: 'projects', action: 'view'}},
  {pattern: '/projects/**', requirement: {resource: 'projects', action: 'view'}},
  // Job create/edit need jobs:edit — listed before the view catch-alls so a
  // viewer can't reach the form by URL (they'd 403 on submit anyway).
  {pattern: '/jobs/new', requirement: {resource: 'jobs', action: 'edit'}},
  {pattern: '/jobs/*/edit', requirement: {resource: 'jobs', action: 'edit'}},
  {pattern: '/jobs', requirement: {resource: 'jobs', action: 'view'}},
  {pattern: '/jobs/**', requirement: {resource: 'jobs', action: 'view'}},
  // Site create/edit need sites:edit — same ordering rationale.
  {pattern: '/sites/new', requirement: {resource: 'sites', action: 'edit'}},
  {pattern: '/sites/*/edit', requirement: {resource: 'sites', action: 'edit'}},
  {pattern: '/sites', requirement: {resource: 'sites', action: 'view'}},
  {pattern: '/sites/**', requirement: {resource: 'sites', action: 'view'}},
  // Transaction ledger — org-wide finance money-movement view (DERP-224).
  // Gated on `reimbursements:export` (Finance + CEO), mirroring the API
  // endpoint. Listed before the `/reimbursements/**` catch-all so the finer
  // export gate wins over the section's broad `view` floor (which every
  // Employee holds) — a viewer would otherwise reach it by URL and 403 on
  // fetch. Same shape and rationale as `/advances/ledger` above.
  {pattern: '/reimbursements/ledger', requirement: {resource: 'reimbursements', action: 'export'}},
  {pattern: '/reimbursements', requirement: {resource: 'reimbursements', action: 'view'}},
  {pattern: '/reimbursements/**', requirement: {resource: 'reimbursements', action: 'view'}},
  // Report-template editor is an admin/config surface. Workers filling a job's
  // report don't hit this route — that's the org-scoped by-project endpoint.
  {
    pattern: '/photo-report-templates',
    requirement: {resource: 'photo_report_templates', action: 'view'},
  },
  {
    pattern: '/photo-report-templates/**',
    requirement: {resource: 'photo_report_templates', action: 'view'},
  },
];

/**
 * Pages a visitor (subcontractor) never gets, whatever their group holds: the
 * dashboard, which reads finance and approvals a visitor can never hold, What's
 * new, which describes staff features, and the Inbox, which lists approvals a
 * visitor can never be asked to decide. Hidden from the rail and
 * redirected away from like any other route the user cannot open.
 *
 * A list keyed on who the user is, not a `ROUTE_REQUIREMENTS` entry, because
 * no permission draws this line: every staff member may open these pages
 * whatever they hold, so any key gating them would shut staff out too. It is
 * the visitor counterpart of an external user's `whitelisted_routes`.
 */
export const VISITOR_HIDDEN_ROUTES: ReadonlyArray<string> = [
  '/',
  '/whats-new',
  '/whats-new/**',
  '/inbox',
  '/inbox/**',
];

/**
 * Where to send someone whose stored home page is closed to them, in order:
 * the dashboard, then the jobs list (the visitor's case: the dashboard is
 * among `VISITOR_HIDDEN_ROUTES`).
 */
const FALLBACK_HOMES: ReadonlyArray<string> = ['/', '/jobs'];

/**
 * The page to send `user` to: their stored home page if they can open it, else
 * the first fallback they can. Without the fallback, anyone whose stored home
 * is closed to them (a visitor created with the default "/", or staff whose
 * home page lost its permission) hits the guard's loop-breaker and is logged
 * out on every sign-in. When nothing is open the stored home is returned, and
 * the loop-breaker does its job.
 */
export function homePathFor(user: WhoamiResponse | null, storedHome?: string | null): string {
  const home = storedHome || user?.home_page || '/';
  if (canAccessRoute(home, user)) return home;
  return FALLBACK_HOMES.find((path) => canAccessRoute(path, user)) ?? home;
}

/**
 * Returns true if `path` matches the given glob `pattern`.
 *
 * Supported syntax:
 * - `*`  matches any sequence of characters except `/` (single segment)
 * - `**` matches any sequence of characters including `/` (any depth)
 * - everything else is matched literally
 */
function matchRoutePattern(path: string, pattern: string): boolean {
  const escapeLiteral = (segment: string): string => segment.replace(/[.+^${}()|[\]\\?]/g, '\\$&');
  const regexBody = pattern
    .split('**')
    .map((doubleSegment) => doubleSegment.split('*').map(escapeLiteral).join('[^/]*'))
    .join('.*');
  return new RegExp(`^${regexBody}$`).test(path);
}

/**
 * Returns true if `user` holds `resource:action`.
 *
 * The single permission lookup in the app: reads the server-supplied
 * `user.permissions` (see CLAUDE.md § Authentication — DERP-249) and lets
 * super-admins pass, since the API exempts them from permission checks rather
 * than issuing them keys. The `usePermission` hook and the route gate below
 * both go through here, so there is one place that knows what "holds a
 * permission" means.
 *
 * Note this is capability-only: an external user's route grants live on
 * `whitelisted_routes` and are honoured by {@link canAccessRoute}, not here.
 */
export function holdsPermission(user: WhoamiResponse, resource: string, action: string): boolean {
  if (user.is_super_admin) return true;
  // `?? []` despite the field being required on the wire: an API build
  // predating DERP-249 omits it, and the safe reading of "no list" is "no
  // permissions" rather than a crash on `undefined.includes`. Falling back to
  // flattening `roles` instead would restore the very bug DERP-249 fixed,
  // precisely when the server is least likely to agree with us. Pinned by
  // `permissions.test.ts::treats a missing list as no permissions`.
  return (user.permissions ?? []).includes(`${resource}:${action}`);
}

/**
 * Returns true if `user` is allowed to access `path`.
 *
 * - No user (not signed in, or the store not yet hydrated) is denied
 * - Super-admins always pass
 * - External users (`is_external: true`) must match an active entry in
 *   `user.whitelisted_routes`
 * - Internal users must hold the permission registered for `path` in
 *   {@link ROUTE_REQUIREMENTS}; routes not registered are unrestricted
 *
 * Everything it needs is on `user` — the permission set used to be passed
 * alongside it, from the days when deriving it meant walking roles + teams.
 * Now that the server ships the effective list, a second argument could only
 * ever disagree with the first, which is the failure mode DERP-249 was. For
 * the same reason `user` is nullable here: callers had all grown the same
 * `user ? canAccessRoute(…) : false` ternary, so what "no user" means belongs
 * to the gate rather than being restated at each call site.
 *
 * The function is a pure check — call it from route guards (`beforeLoad`),
 * runtime layout effects, or nav-link visibility logic.
 */
export function canAccessRoute(
  path: string,
  user: WhoamiResponse | null,
  rules: ReadonlyArray<RouteRule> = ROUTE_REQUIREMENTS,
): boolean {
  if (!user) return false;
  if (user.is_super_admin) return true;

  if (user.is_external) {
    return user.whitelisted_routes.some(
      (entry) => entry.is_active && matchRoutePattern(path, entry.route_pattern),
    );
  }

  if (
    user.is_visitor &&
    VISITOR_HIDDEN_ROUTES.some((pattern) => matchRoutePattern(path, pattern))
  ) {
    return false;
  }

  const rule = rules.find((entry) => matchRoutePattern(path, entry.pattern));
  if (!rule) return true;

  return holdsPermission(user, rule.requirement.resource, rule.requirement.action);
}
